/**
 * Per-port VNET conformance for the PON inventory grid.
 *
 * A healthy PON port is uniform: every ONU carries the same set of VNET
 * profiles, because they were all provisioned from the same site
 * template. The interesting thing is therefore never the VNET list
 * itself — it is the ONU that *differs* from its neighbours. An ONU
 * with an extra VNET was hand-edited; one missing a VNET was
 * provisioned before the template changed, or half-failed.
 *
 * Two rules make this honest rather than noisy:
 *
 * 1. **The signature is the set, not the count.** Two ONUs can both
 *    have three VNETs and still disagree about which three. Comparing
 *    counts alone would call that uniform.
 * 2. **A baseline requires a unique mode.** If a port splits 15/15
 *    between two signatures there is no "normal", so nothing is
 *    flagged — calling half a port anomalous is worse than saying
 *    nothing. Only a strict plurality establishes a norm.
 *
 * ONUs missing from the backup parse are ``unknown``, never outliers:
 * the backup simply may not cover them (same principle as
 * ``clearImpact.ts`` — absence of evidence is not evidence).
 */

export type OnuServiceLike = { vnet_profile: string | null };

/** Sorted, de-duplicated VNET profile names for one ONU. */
export function vnetSignature(services: OnuServiceLike[] | undefined): string[] {
  if (!services?.length) return [];
  const names = services
    .map((s) => s.vnet_profile)
    .filter((n): n is string => !!n);
  return [...new Set(names)].sort();
}

/** Stable key for grouping signatures. */
export function signatureKey(sig: string[]): string {
  return sig.join(" + ");
}

export type PortVnetProfile = {
  /** The modal signature, or null when no unique mode exists. */
  norm: string[] | null;
  /** ONUs whose signature equals the norm. */
  conforming: string[];
  /** ONUs that have backup data but disagree with the norm. */
  outliers: string[];
  /** ONUs absent from the backup parse — no verdict possible. */
  unknown: string[];
};

export function analysePort(
  displayIds: string[],
  services: Record<string, OnuServiceLike[]> | undefined,
): PortVnetProfile {
  const known: { id: string; key: string }[] = [];
  const unknown: string[] = [];

  for (const id of displayIds) {
    const entry = services?.[id];
    // An ONU present in the parse but with zero services is a real
    // finding (provisioned with nothing), so it counts as known.
    if (entry === undefined) unknown.push(id);
    else known.push({ id, key: signatureKey(vnetSignature(entry)) });
  }

  const tally = new Map<string, number>();
  for (const k of known) tally.set(k.key, (tally.get(k.key) ?? 0) + 1);

  // Unique mode only. Sorted by count desc, then key for determinism.
  const ranked = [...tally.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  const hasUniqueMode =
    ranked.length > 0 && (ranked.length === 1 || ranked[0][1] > ranked[1][1]);

  if (!hasUniqueMode) {
    return { norm: null, conforming: [], outliers: [], unknown };
  }

  const normKey = ranked[0][0];
  return {
    norm: normKey === "" ? [] : normKey.split(" + "),
    conforming: known.filter((k) => k.key === normKey).map((k) => k.id),
    outliers: known.filter((k) => k.key !== normKey).map((k) => k.id),
    unknown,
  };
}

export type VnetDiff = {
  /** Profiles this ONU has that the norm does not. */
  extra: string[];
  /** Profiles the norm has that this ONU is missing. */
  missing: string[];
};

/**
 * How one ONU departs from its port's baseline.
 *
 * Needed because the count alone is often identical: the most common
 * real drift is a *substituted* profile (``VNET30`` where the port
 * expects ``VNET30-PPPOE``), which leaves the count at 3 and would
 * otherwise read as an unexplained amber cell.
 */
export function vnetDiff(sig: string[], norm: string[] | null): VnetDiff {
  if (!norm) return { extra: [], missing: [] };
  const have = new Set(sig);
  const want = new Set(norm);
  return {
    extra: sig.filter((v) => !want.has(v)),
    missing: norm.filter((v) => !have.has(v)),
  };
}

/** "+VNET30 −VNET30-PPPOE" — compact enough for a dense table cell. */
export function formatVnetDiff(d: VnetDiff): string {
  return [
    ...d.extra.map((v) => `+${v}`),
    ...d.missing.map((v) => `−${v}`),
  ].join(" ");
}

export type ChassisVnetProfile = {
  /** Modal signature across every port that has one. */
  norm: string[] | null;
  outlierCount: number;
  /** Ports carrying at least one outlier, in the order supplied. */
  portsWithOutliers: string[];
  unknownCount: number;
};

export function analyseChassis(
  ports: { port: string; displayIds: string[] }[],
  services: Record<string, OnuServiceLike[]> | undefined,
): ChassisVnetProfile {
  const tally = new Map<string, number>();
  const portsWithOutliers: string[] = [];
  let outlierCount = 0;
  let unknownCount = 0;

  for (const p of ports) {
    const a = analysePort(p.displayIds, services);
    outlierCount += a.outliers.length;
    unknownCount += a.unknown.length;
    if (a.outliers.length > 0) portsWithOutliers.push(p.port);
    if (a.norm) {
      const k = signatureKey(a.norm);
      // Weight by conforming ONUs so a 2-ONU port cannot outvote a
      // 30-ONU one.
      tally.set(k, (tally.get(k) ?? 0) + a.conforming.length);
    }
  }

  const ranked = [...tally.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  const hasUniqueMode =
    ranked.length > 0 && (ranked.length === 1 || ranked[0][1] > ranked[1][1]);

  return {
    norm: hasUniqueMode
      ? ranked[0][0] === ""
        ? []
        : ranked[0][0].split(" + ")
      : null,
    outlierCount,
    portsWithOutliers,
    unknownCount,
  };
}
